export interface PageMetadata {
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
}

export interface PageHandle {
  metadata: PageMetadata;
}

export const homeHandle = {
  metadata: {
    title: 'BidetNY - Home',
    description: 'Welcome to BidetNY, your premier source for modern bidet solutions in New York.',
    keywords: ['bidet', 'new york', 'home hygiene', 'smart toilet'],
    ogTitle: 'BidetNY - Perfect Bidet Solutions',
    ogDescription: 'Discover the future of personal hygiene with BidetNY. Explore our range of high-quality bidets.',
  },
} satisfies PageHandle;

export const aboutHandle = {
  metadata: {
    title: 'About Us - BidetNY',
    description: 'Learn about the mission, values, and team behind BidetNY.',
    ogTitle: 'About BidetNY',
    ogDescription: 'Discover the story and mission of BidetNY.',
  },
} satisfies PageHandle;

export const pricingHandle = {
  metadata: {
    title: 'Pricing - BidetNY',
    description: 'Find the perfect bidet model for your budget.',
    ogTitle: 'BidetNY Pricing Plans',
    ogDescription: 'Explore pricing options for your bidet and installation. Starting at only $85 for everything included.',
  },
} satisfies PageHandle;

export const bidetsHandle = {
  metadata: {
    title: 'Our Bidets - BidetNY',
    description: 'Explore our wide range of high-quality bidet products designed for comfort and hygiene.',
    ogTitle: 'Explore BidetNY Products',
    ogDescription: 'Browse our selection of advanced bidet models.',
  },
} satisfies PageHandle;

export const careersHandle = {
  metadata: {
    title: 'Careers - BidetNY',
    description: 'Join the BidetNY team and help us revolutionize personal hygiene. Explore open positions.',
    ogTitle: 'Work With Us - BidetNY Careers',
    ogDescription: 'Find exciting career opportunities at BidetNY.',
  },
} satisfies PageHandle;

export const careerDetailHandle = {
  metadata: {
    title: 'Career Opportunity - BidetNY',
    description: 'Explore this exciting career opportunity at BidetNY.',
  },
} satisfies PageHandle;

export const orderHandle = {
  metadata: {
    title: 'Order Your Bidet - BidetNY',
    description: 'Complete your bidet order securely and easily with BidetNY.',
  },
} satisfies PageHandle;

export const orderingPausedHandle = {
  metadata: {
    title: 'Ordering Temporarily Paused - BidetNY',
    description: "We're temporarily not accepting new orders. Please check back soon.",
  },
} satisfies PageHandle;

export const orderConfirmationHandle = {
  metadata: {
    title: 'Order Confirmation - BidetNY',
    description: 'Thank you for your order with BidetNY. Your confirmation details are here.',
  },
} satisfies PageHandle;

export const surveyHandle = {
  metadata: {
    title: 'Customer Survey - BidetNY',
    description: 'Help us improve our products and services by taking our short survey.',
  },
} satisfies PageHandle;

export const termsHandle = {
  metadata: {
    title: 'Terms of Service - BidetNY',
    description: 'Read the official Terms of Service for BidetNY.',
  },
} satisfies PageHandle;

export const privacyHandle = {
  metadata: {
    title: 'Privacy Policy - BidetNY',
    description: 'Understand how BidetNY collects, uses, and protects your personal data.',
  },
} satisfies PageHandle;

export const questionsHandle = {
  metadata: {
    title: 'FAQ - BidetNY',
    description: 'Find answers to frequently asked questions about bidets, installation, and BidetNY services.',
    ogTitle: 'BidetNY FAQ',
    ogDescription: 'Get answers to your questions about BidetNY.',
  },
} satisfies PageHandle;

export const landingHandle = {
  metadata: {
    title: 'Special Offer - BidetNY Landing Page',
    description: 'Discover exclusive offers on BidetNY products through our special landing page.',
  },
} satisfies PageHandle;

export const complaintHandle = {
  metadata: {
    title: 'Submit a Complaint - BidetNY',
    description: 'Lodge a complaint or provide feedback to BidetNY customer service.',
  },
} satisfies PageHandle;

export const paymentHandle = {
  metadata: {
    title: 'Payment - BidetNY',
  },
} satisfies PageHandle;

export const notFoundHandle = {
  metadata: {
    title: '404 - Page Not Found',
    description: 'The page you are looking for could not be found.',
  },
} satisfies PageHandle;

/**
 * React Router's route-config `handle` values are build-time metadata and are
 * not present in the root route's server-side meta matches. Resolve the same
 * canonical handles from the matched pathname so SSR and client navigation
 * produce identical per-route tags.
 */
export function metadataHandleForPath(pathname: string): PageHandle {
  if (pathname === '/') return homeHandle;
  if (pathname === '/about') return aboutHandle;
  if (pathname === '/pricing') return pricingHandle;
  if (pathname === '/bidets' || pathname.startsWith('/bidets/')) return bidetsHandle;
  if (pathname === '/careers') return careersHandle;
  if (pathname.startsWith('/careers/')) return careerDetailHandle;
  if (pathname === '/order/confirmation') return orderConfirmationHandle;
  if (pathname === '/order') return orderHandle;
  if (pathname === '/ordering-paused') return orderingPausedHandle;
  if (pathname === '/survey') return surveyHandle;
  if (pathname === '/terms') return termsHandle;
  if (pathname === '/privacy') return privacyHandle;
  if (pathname === '/questions') return questionsHandle;
  if (pathname === '/landing/1') return landingHandle;
  if (pathname === '/complaint') return complaintHandle;
  if (pathname.startsWith('/pay/')) return paymentHandle;
  return notFoundHandle;
}
